


import React, { useState, useEffect, useMemo } from "react";
import api from "../../api/api"; // ✅ Use your central api.js
import Heading from "../../components/ui/Heading"; 
import { PlusCircle, FileEdit, X as CloseIcon } from "lucide-react";

import SearchInput from "../../components/ui/SearchInput";
import Button from '../../components/ui/Button';

export default function App() {
    const [vouchers, setVouchers] = useState([]);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingVoucher, setEditingVoucher] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [viewMode, setViewMode] = useState("card"); // 'table' or 'card'

   const fetchData = async () => {
  try {
    setIsLoading(true);
    setError(null);

    const data = await api.ExpenseList("Admin");
    console.log("ExpenseList data:", data);

   const tableData = data?.Table || [];

    const mappedVouchers = tableData.map((item, index) => ({
    id: item.Entid || item.VrNo || index,
    entId: item.Entid,
    vrNo: item.VrNo,
    date: item.VrDate ? item.VrDate.split("T")[0] : "",
    paymentMode: item.Mode || item.PayMode || "",
    paidThrough: item.PaidThrough || "",
    paidFor: item.PaidFor || item.PaidRefName || "",
    bankName: item.BankName || "",
    txnRefNo: item.TxnRefNo || item.CheQueNo || item.DocNo || "",
    particulars: item.Particulars || "",
    preparedBy: item.PreparedBy || "",
    checkedBy: item.CheckedBy || "",
    expenseName: item.ExpName || "",
    expenseGroup: item.ExpGroup || item.ExpGrpName || "",
    amount: String(item.Amount || item.VrAmount || "0"),
}));

    setVouchers(mappedVouchers);
  } catch (err) {
    console.error("Error fetching expenses:", err);
    setError("Failed to load vouchers. The API might be down or unreachable.");
  } finally {
    setIsLoading(false);
  }
};

    useEffect(() => {
        fetchData();
    }, []);

    const filteredVouchers = useMemo(() => {
        if (!searchTerm) return vouchers;
        const lowercasedTerm = searchTerm.toLowerCase();
        return vouchers.filter(
            v =>
                v.vrNo?.toLowerCase().includes(lowercasedTerm) ||
                v.expenseName?.toLowerCase().includes(lowercasedTerm) ||
                v.paidFor?.toLowerCase().includes(lowercasedTerm) ||
                v.paymentMode?.toLowerCase().includes(lowercasedTerm) ||
                v.amount?.toString().includes(lowercasedTerm)
        );
    }, [searchTerm, vouchers]);

    const handleOpenForm = async (voucher = null) => {
        if (!voucher) {
            setEditingVoucher(null);
            setIsFormOpen(true);
            return;
        }

        if (!voucher.entId) {
            setError("Cannot edit: Voucher ID is missing.");
            console.error("Attempted to edit a voucher with a missing EntId:", voucher);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            const res = await api.EditExpenseList(voucher.entId);
           const data = res?.Table?.[0]; // ✅ Use actual API response structure
if (!data) throw new Error("No voucher data found.");

         setEditingVoucher({
     entId: data.EntId || 0,       
    expenseId: data.ExpId || 0,
    bankId: data.BankId || 0,
    paidRefId: data.PaidRefid || 0,
    vrNo: data.VrNo || "",
    date: data.VrDate ? data.VrDate.split("T")[0] : "",
    paymentMode: data.Mode || "CASH",
    paidThrough: data.PaidThrough || "",
    paidFor: data.PaidFor || data.PaidRefName || "",
    bankName: data.BankName || "",
    txnRefNo: data.TxnRefNo || data.CheQueNo || data.DocNo || "",
    particulars: data.Particulars || "",
    preparedBy: data.PreparedBy || "Admin",
    checkedBy: data.CheckedBy || "Admin",
    expenseName: data.ExpName || "",
    expenseGroup: data.ExpGroup || data.ExpGrpName || "",
    amount: String(data.Amount || data.VrAmount || "0"),
});


            setIsFormOpen(true);
        } catch (err) {
            console.error("Failed to fetch edit data:", err);
            setError("Failed to load voucher details for editing.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseForm = () => {
        setEditingVoucher(null);
        setIsFormOpen(false);
    };

   // In App component
const handleSaveVoucher = async (voucherData) => {
  try {
    setError(null);

    const payloadToSave = {
      EntId: Number(voucherData.entId || 0), // 0 = new, >0 = update
      ExpGrpName: voucherData.expenseGroup || "",
      ExpId: Number(voucherData.expenseId || 0),
      ExpName: voucherData.expenseName || "",
      Particulars: voucherData.particulars || "",
      BankId: Number(voucherData.bankId || 0),
      BankName: voucherData.bankName || "",
      PayMode: voucherData.paymentMode || "CASH",
      VrNo: voucherData.vrNo || "",
      VrDate: voucherData.date || new Date().toISOString().split("T")[0],
      VrAmount: parseFloat(voucherData.amount || 0),
      PaidRefId: Number(voucherData.paidRefId || 0),
      PaidRefName: voucherData.paidFor || "",
      PaidThrough: voucherData.paidThrough || "",
     SqlAction: Number(voucherData.entId) > 0 ? "update" : "insert",
      DepositBank: "",
      CheckNo: voucherData.txnRefNo || "",
      PreparedBy: voucherData.preparedBy || "Admin",
      CheckedBy: voucherData.checkedBy || "Admin",
    };

    console.log("Saving payload:", payloadToSave);

    await api.SaveExpenseAPI(payloadToSave);

    handleCloseForm();
    await fetchData();
  } catch (err) {
    console.error("Error saving Expense:", err);
    setError("Failed to save voucher. Please try again.");
    throw err;
  }
};



    return (
   <div className="flex flex-col h-full">
    {/* ---------- HEADER ---------- */}
 
<div className="px-4 py-4 bg-gray-100 border-b">
  {/* Title */}
  <div className="mb-3">
    <Heading level={2}>Expense Management</Heading>
  </div>

  {/* Actions */}
  <div className="flex items-center gap-2 mb-3">
    {/* New Button */}
    <Button
      onClick={() => handleOpenForm()}
            className="flex items-center gap-2 px-3 py-1.5 text-white bg-gradient-to-r from-blue-500 to-indigo-600
              rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg transition-all duration-300 ease-in-out"
    >
      <PlusCircle size={18} /> New
    </Button>

    <div className="flex-1" />

    {/* View Toggle */}
    <div className="flex items-center justify-center p-1 bg-white-100 rounded-lg">
            {/* Grid */}
            <button
              onClick={() => setViewMode("card")}
              className={`p-2 rounded ${viewMode === "card" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h6v6H4V4z M14 4h6v6h-6V4z M4 14h6v6H4v-6z M14 14h6v6h-6v-6z" />
              </svg>
            </button>

            {/* Table */}
            <button
              onClick={() => setViewMode("table")}
              className={`p-2 rounded ${viewMode === "table" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
  </div>

  {/* Search */}
  <div className="flex items-center gap-2">

   <Button
      variant="outline"
      className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
      onClick={() => setViewMode("card")} // ✅ works fine
    >
      ⬅
    </Button>



    <SearchInput
      placeholder="Search expenses..."
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
    />
  </div>
</div>

    {/* ---------- MAIN BODY ---------- */}
    <div className="p-4">
      {isFormOpen && (
        <ExpenseForm
          voucher={editingVoucher}
          onSave={handleSaveVoucher}
          onClose={handleCloseForm}
        />
      )}

      {isLoading ? (
   <div className="p-10 text-center">
     <p className="text-gray-500">Loading Expense...</p>
   </div>
) : viewMode === "table" ? (
   <ExpenseTable vouchers={filteredVouchers} onEdit={handleOpenForm} />
) : (
   <ExpenseCardList vouchers={filteredVouchers} onEdit={handleOpenForm} />
)}

    </div>
  </div>
);

}

/* ---------------- Expense Form ---------------- */
// ---------------- Expense Form ----------------
const ExpenseForm = ({ voucher, onSave, onClose }) => {
  const [accounts, setAccounts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const defaultFormState = useMemo(() => ({
    vrNo: "",
    date: new Date().toISOString().split("T")[0],
    paymentMode: "CASH",
    paidThrough: "Admin",
    paidFor: "",
    bankName: "",
    txnRefNo: "",
    particulars: "",
    preparedBy: "Admin",
    checkedBy: "Admin",
    expenseName: "",
    expenseGroup: "",
    amount: "",
    entId: 0,
    expenseId: 0,
    bankId: 0,
    paidRefId: 0,
  }), []);

  const [formData, setFormData] = useState(defaultFormState);

useEffect(() => {
    if (voucher) {
        setFormData({
            ...defaultFormState,
            ...voucher,
            entId: voucher.entId || 0, // crucial!
            expenseId: voucher.expenseId || 0,
            bankId: voucher.bankId || 0,
            paidRefId: voucher.paidRefId || 0,
        });
    } else {
        setFormData(defaultFormState);
    }
}, [voucher, defaultFormState]);



  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const res = await api.GetExpenseVal("Admin");
        const data = res?.GetExpenseValAPIjs || res;

        setAccounts((data.Table3 || []).map(a => ({
          id: a.HeadId,
          name: a.HeadName,
          group: a.AccGroup,
          accId: a.AccId
        })));

        setStaff((data.Table || []).map(s => ({
          id: s.Eid,
          name: s.Ename,
          code: s.Ecode
        })));

        setPaymentModes((data.Table1 || []).map(p => p.PMName));

        setBanks((data.Table2 || []).map(b => ({
          id: b.BankId,
          name: b.BankName,
          accNo: b.AccNo
        })));

      } catch (err) {
        console.error("Failed to fetch form data:", err);
      }
    };
    fetchFormData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updates = { [name]: value };

    if (name === "expenseName") {
      const selectedAccount = accounts.find(acc => acc.name === value);
      updates.expenseGroup = selectedAccount ? selectedAccount.group : "";
      updates.expenseId = selectedAccount ? selectedAccount.id : 0;
    } else if (name === "bankName") {
      const selectedBank = banks.find(b => b.name === value);
      updates.bankId = selectedBank ? selectedBank.id : 0;
    }

    setFormData(prev => ({ ...prev, ...updates }));
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSaving(true);
  try {
    await onSave(formData); // ✅ formData is mapped in handleSaveVoucher
    setIsSaving(false);
  } catch (error) {
    console.error("Error in handleSubmit:", error);
    setIsSaving(false);
  }
};


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="p-6 sm:p-8">
          {/* Header */}
          <div className="flex items-center justify-between pb-4 mb-6 border-b">
            <h2 className="text-2xl font-bold text-gray-700">
              {voucher ? "Edit Expense" : "Create New Expense"}
            </h2>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <CloseIcon />
            </button>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-4">
              <FormDatalist
                label="Expense Name"
                name="expenseName"
                value={formData.expenseName || ""}
                onChange={handleChange}
                options={accounts.map(a => a.name)}
                required
              />
              <FormInput label="Expense Group" name="expenseGroup" value={formData.expenseGroup} readOnly disabled />
              <FormInput label="Amount" name="amount" type="number" value={formData.amount} onChange={handleChange} required />
            </div>

            <div className="space-y-4">
              <FormInput label="Date" name="date" type="date" value={formData.date} onChange={handleChange} required />
              <FormSelect label="Payment Mode" name="paymentMode" value={formData.paymentMode} onChange={handleChange} options={paymentModes} />
              {formData.paymentMode !== "CASH" && (
                <>
                  <FormDatalist label="Bank Name" name="bankName" value={formData.bankName} onChange={handleChange} options={banks.map(b => b.name)} required />
                  <FormInput label="Txn Ref No / Chq No" name="txnRefNo" value={formData.txnRefNo} onChange={handleChange} />
                </>
              )}
              <FormInput label="Paid For" name="paidFor" value={formData.paidFor} onChange={handleChange} required />
            </div>

            <div className="space-y-4">
              <FormDatalist label="Paid Through" name="paidThrough" value={formData.paidThrough} onChange={handleChange} options={staff.map(s => s.name)} />
              <FormDatalist label="Prepared By" name="preparedBy" value={formData.preparedBy} onChange={handleChange} options={staff.map(s => s.name)} />
              <FormDatalist label="Checked By" name="checkedBy" value={formData.checkedBy} onChange={handleChange} options={staff.map(s => s.name)} />
            </div>

            <div className="md:col-span-2 lg:col-span-3">
              <FormTextArea label="Particulars / Remarks" name="particulars" value={formData.particulars} onChange={handleChange} />
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-6 mt-8 border-t">
            <button type="submit" disabled={isSaving} className="px-3 py-1 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600">
              {isSaving ? "Saving..." : "Save Expense"}
            </button>
            <button type="button" onClick={onClose} className="px-3 py-1 text-sm font-medium text-white bg-gray-500 rounded hover:bg-gray-600">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


/* ---------------- Reusable Form & List Components (No Changes) ---------------- */
const ExpenseTable = ({ vouchers, onEdit }) => {
    if (!vouchers || vouchers.length === 0)
        return (
            <div className="p-10 text-center bg-white rounded-lg shadow-md">
                <p>No expenses found. </p>
            </div>
        );

    return (
        <div className="overflow-x-auto bg-white shadow-md rounded-xl">
            <table className="w-full text-sm text-left text-gray-500">
                <thead className="text-sm text-gray-900 uppercase bg-gray-100">
                    <tr>
                        <th className="px-6 py-3">Vr No</th>
                        <th className="px-6 py-3">Date</th>
                        <th className="px-6 py-3">Expense Name</th>
                        <th className="px-6 py-3">Paid For</th>
                        <th className="px-6 py-3">Mode</th>
                        <th className="px-6 py-3 text-right">Amount</th>
                        <th className="px-6 py-3 text-center">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {vouchers.map((v) => (
                        <tr
                            key={v.id}
                            className="bg-white border-b hover:bg-gray-50"
                        >
                            <td className="px-6 py-4 font-medium text-gray-900">
                                {v.vrNo}
                            </td>
                            <td className="px-6 py-4">{v.date}</td>
                            <td className="px-6 py-4">{v.expenseName}</td>
                            <td className="px-6 py-4">{v.paidFor}</td>
                            <td className="px-6 py-4">
                                <span
                                    className={`px-2 py-1 text-xs font-medium rounded-full ${v.paymentMode === "CASH"
                                            ? "bg-green-100 text-green-800"
                                            : "bg-blue-100 text-blue-800"
                                        }`}
                                >
                                    {v.paymentMode}
                                </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                                ₹
                                {parseFloat(v.amount || 0).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                            </td>
                            <td className="px-6 py-4 text-center">
                                <button
                                    onClick={() => onEdit(v)}
                                     className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium text-white 
             bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
             hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
             transition-all duration-300 ease-in-out"
                                >
                                     <FileEdit className="w-5 h-5" />
                                    <span className="font-medium">Edit</span>
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const ExpenseCardList = ({ vouchers, onEdit }) => {
    if (!vouchers || vouchers.length === 0)
        return (
            <div className="p-10 text-center bg-white rounded-lg shadow-md">
                <p>No expenses found. </p>
            </div>
        );

    return (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
            {vouchers.map((v) => (
                <div
                    key={v.id}
                    className="flex flex-col p-5 transition-shadow bg-white shadow-md rounded-xl hover:shadow-lg"
                >
                    <div className="flex items-start justify-between mb-3">
                        <h3 className="flex-1 pr-2 text-sm font-bold text-grey-700">
                            {v.expenseName}
                        </h3>
                       
                    </div>
                    <div className="mb-4 space-y-1 text-sm text-gray-700">
                        <p>
                            <strong>Vr No:</strong> {v.vrNo}
                        </p>
                        <p>
                            <strong>Date:</strong> {v.date}
                        </p>
                        <p>
                            <strong>Paid To:</strong> {v.paidFor}
                        </p>
                        <p>
                            <strong>Mode:</strong>{" "}
                            <span
                                className={`px-2 py-0.5 text-xs font-medium rounded-full ${v.paymentMode === "CASH"
                                        ? "bg-green-100 text-green-800"
                                        : "bg-blue-100 text-blue-800"
                                    }`}
                            >
                                {v.paymentMode}
                            </span>
                        </p>
                        {v.paymentMode !== "CASH" && v.bankName && (
                            <p>
                                <strong>Bank:</strong> {v.bankName}
                            </p>
                        )}
                    </div>
                    <div className="mt-auto text-right">
                        <button
                            onClick={() => onEdit(v)}
                             className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium text-white 
             bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
             hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
             transition-all duration-300 ease-in-out"
                        >
                           <FileEdit className="w-5 h-5" />
                           <span className="font-medium">Edit</span>
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
};

const FormInput = ({ label, ...props }) => (
    <div>
        <label className="block mb-1 text-sm font-medium text-gray-600">
            {label}
        </label>
        <input
            {...props}
            className="w-full px-3 py-2 transition border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
        />
    </div>
);

const FormDatalist = ({ label, name, value, onChange, options, ...props }) => (
    <div>
        <label
            htmlFor={name}
            className="block mb-1 text-sm font-medium text-gray-600"
        >
            {label}
        </label>
        <input
            id={name}
            name={name}
            list={`${name}-list`}
            value={value || ""}
            onChange={onChange}
            {...props}
            className="w-full px-3 py-2 transition border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
        />
        <datalist id={`${name}-list`}>
            {options.map((opt) => (
                <option key={opt} value={opt} />
            ))}
        </datalist>
    </div>
);

const FormSelect = ({ label, options, ...props }) => (
    <div>
        <label className="block mb-1 text-sm font-medium text-gray-600">
            {label}
        </label>
        <select
            {...props}
            className="w-full px-3 py-2 transition border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
        >
            {options.map((opt) => (
                <option key={opt} value={opt}>
                    {opt}
                </option>
            ))}
        </select>
    </div>
);

const FormTextArea = ({ label, ...props }) => (
    <div>
        <label className="block mb-1 text-sm font-medium text-gray-600">
            {label}
        </label>
        <textarea
            {...props}
            rows="3"
            className="w-full px-3 py-2 transition border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
        />
    </div>
);




// import React, { useState, useEffect, useMemo } from "react";
// import api from "../../api/api"; // ✅ Use your central api.js
// import Heading from "../../components/ui/Heading"; 
// import { PlusCircle, FileEdit, X as CloseIcon } from "lucide-react";

// import SearchInput from "../../components/ui/SearchInput";
// import Button from '../../components/ui/Button';

// export default function App() {
//     const [vouchers, setVouchers] = useState([]);
//     const [isFormOpen, setIsFormOpen] = useState(false);
//     const [editingVoucher, setEditingVoucher] = useState(null);
//     const [isLoading, setIsLoading] = useState(true);
//     const [error, setError] = useState(null);
//     const [searchTerm, setSearchTerm] = useState("");
//     const [viewMode, setViewMode] = useState("card"); // 'table' or 'card'

//    const fetchData = async () => {
//   try {
//     setIsLoading(true);
//     setError(null);

//     const data = await api.ExpenseList("Admin");
//     console.log("ExpenseList data:", data);

//    const tableData = data?.Table || [];

//     const mappedVouchers = tableData.map((item, index) => ({
//     id: item.Entid || item.VrNo || index,
//     entId: item.Entid,
//     vrNo: item.VrNo,
//     date: item.VrDate ? item.VrDate.split("T")[0] : "",
//     paymentMode: item.Mode || item.PayMode || "",
//     paidThrough: item.PaidThrough || "",
//     paidFor: item.PaidFor || item.PaidRefName || "",
//     bankName: item.BankName || "",
//     txnRefNo: item.TxnRefNo || item.CheQueNo || item.DocNo || "",
//     particulars: item.Particulars || "",
//     preparedBy: item.PreparedBy || "",
//     checkedBy: item.CheckedBy || "",
//     expenseName: item.ExpName || "",
//     expenseGroup: item.ExpGroup || item.ExpGrpName || "",
//     amount: String(item.Amount || item.VrAmount || "0"),
// }));

//     setVouchers(mappedVouchers);
//   } catch (err) {
//     console.error("Error fetching expenses:", err);
//     setError("Failed to load vouchers. The API might be down or unreachable.");
//   } finally {
//     setIsLoading(false);
//   }
// };

//     useEffect(() => {
//         fetchData();
//     }, []);

//     const filteredVouchers = useMemo(() => {
//         if (!searchTerm) return vouchers;
//         const lowercasedTerm = searchTerm.toLowerCase();
//         return vouchers.filter(
//             v =>
//                 v.vrNo?.toLowerCase().includes(lowercasedTerm) ||
//                 v.expenseName?.toLowerCase().includes(lowercasedTerm) ||
//                 v.paidFor?.toLowerCase().includes(lowercasedTerm) ||
//                 v.paymentMode?.toLowerCase().includes(lowercasedTerm) ||
//                 v.amount?.toString().includes(lowercasedTerm)
//         );
//     }, [searchTerm, vouchers]);

//     const handleOpenForm = async (voucher = null) => {
//         if (!voucher) {
//             setEditingVoucher(null);
//             setIsFormOpen(true);
//             return;
//         }

//         if (!voucher.entId) {
//             setError("Cannot edit: Voucher ID is missing.");
//             console.error("Attempted to edit a voucher with a missing EntId:", voucher);
//             return;
//         }

//         try {
//             setIsLoading(true);
//             setError(null);
//             const res = await api.EditExpenseList(voucher.entId);
//            const data = res?.Table?.[0]; // ✅ Use actual API response structure
// if (!data) throw new Error("No voucher data found.");

//          setEditingVoucher({
//     entId: data.Entid || 0,      // must be actual ID from API
//     expenseId: data.ExpId || 0,
//     bankId: data.BankId || 0,
//     paidRefId: data.PaidRefid || 0,
//     vrNo: data.VrNo || "",
//     date: data.VrDate ? data.VrDate.split("T")[0] : "",
//     paymentMode: data.Mode || "CASH",
//     paidThrough: data.PaidThrough || "",
//     paidFor: data.PaidFor || data.PaidRefName || "",
//     bankName: data.BankName || "",
//     txnRefNo: data.TxnRefNo || data.CheQueNo || data.DocNo || "",
//     particulars: data.Particulars || "",
//     preparedBy: data.PreparedBy || "Admin",
//     checkedBy: data.CheckedBy || "Admin",
//     expenseName: data.ExpName || "",
//     expenseGroup: data.ExpGroup || data.ExpGrpName || "",
//     amount: String(data.Amount || data.VrAmount || "0"),
// });


//             setIsFormOpen(true);
//         } catch (err) {
//             console.error("Failed to fetch edit data:", err);
//             setError("Failed to load voucher details for editing.");
//         } finally {
//             setIsLoading(false);
//         }
//     };

//     const handleCloseForm = () => {
//         setEditingVoucher(null);
//         setIsFormOpen(false);
//     };

//    // In App component
// const handleSaveVoucher = async (voucherData) => {
//   try {
//     setError(null);

//     const payloadToSave = {
//       EntId: Number(voucherData.entId || 0), // 0 = new, >0 = update
//       ExpGrpName: voucherData.expenseGroup || "",
//       ExpId: Number(voucherData.expenseId || 0),
//       ExpName: voucherData.expenseName || "",
//       Particulars: voucherData.particulars || "",
//       BankId: Number(voucherData.bankId || 0),
//       BankName: voucherData.bankName || "",
//       PayMode: voucherData.paymentMode || "CASH",
//       VrNo: voucherData.vrNo || "",
//       VrDate: voucherData.date || new Date().toISOString().split("T")[0],
//       VrAmount: parseFloat(voucherData.amount || 0),
//       PaidRefId: Number(voucherData.paidRefId || 0),
//       PaidRefName: voucherData.paidFor || "",
//       PaidThrough: voucherData.paidThrough || "",
//      SqlAction: Number(voucherData.entId) > 0 ? "update" : "insert",
//       DepositBank: "",
//       CheckNo: voucherData.txnRefNo || "",
//       PreparedBy: voucherData.preparedBy || "Admin",
//       CheckedBy: voucherData.checkedBy || "Admin",
//     };

//     console.log("Saving payload:", payloadToSave);

//     await api.SaveExpenseAPI(payloadToSave);

//     handleCloseForm();
//     await fetchData();
//   } catch (err) {
//     console.error("Error saving Expense:", err);
//     setError("Failed to save voucher. Please try again.");
//     throw err;
//   }
// };



//     return (
//    <div className="flex flex-col h-full">
//     {/* ---------- HEADER ---------- */}
 
// <div className="px-4 py-4 bg-gray-100 border-b">
//   {/* Title */}
//   <div className="mb-3">
//     <Heading level={2}>Expense Management</Heading>
//   </div>

//   {/* Actions */}
//   <div className="flex items-center gap-2 mb-3">
//     {/* New Button */}
//     <Button
//       onClick={() => handleOpenForm()}
//             className="flex items-center gap-2 px-3 py-1.5 text-white bg-gradient-to-r from-blue-500 to-indigo-600
//               rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg transition-all duration-300 ease-in-out"
//     >
//       <PlusCircle size={18} /> New
//     </Button>

//     <div className="flex-1" />

//     {/* View Toggle */}
//     <div className="flex items-center justify-center p-1 bg-white-100 rounded-lg">
//             {/* Grid */}
//             <button
//               onClick={() => setViewMode("card")}
//               className={`p-2 rounded ${viewMode === "card" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
//             >
//               <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h6v6H4V4z M14 4h6v6h-6V4z M4 14h6v6H4v-6z M14 14h6v6h-6v-6z" />
//               </svg>
//             </button>

//             {/* Table */}
//             <button
//               onClick={() => setViewMode("table")}
//               className={`p-2 rounded ${viewMode === "table" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
//             >
//               <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
//               </svg>
//             </button>
//           </div>
//   </div>

//   {/* Search */}
//   <div className="flex items-center gap-2">

//    <Button
//       variant="outline"
//       className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
//       onClick={() => setViewMode("card")} // ✅ works fine
//     >
//       ⬅
//     </Button>



//     <SearchInput
//       placeholder="Search expenses..."
//       value={searchTerm}
//       onChange={(e) => setSearchTerm(e.target.value)}
//     />
//   </div>
// </div>

//     {/* ---------- MAIN BODY ---------- */}
//     <div className="p-4">
//       {isFormOpen && (
//         <ExpenseForm
//           voucher={editingVoucher}
//           onSave={handleSaveVoucher}
//           onClose={handleCloseForm}
//         />
//       )}

//       {isLoading ? (
//    <div className="p-10 text-center">
//      <p className="text-gray-500">Loading Expense...</p>
//    </div>
// ) : viewMode === "table" ? (
//    <ExpenseTable vouchers={filteredVouchers} onEdit={handleOpenForm} />
// ) : (
//    <ExpenseCardList vouchers={filteredVouchers} onEdit={handleOpenForm} />
// )}

//     </div>
//   </div>
// );

// }

// /* ---------------- Expense Form ---------------- */
// // ---------------- Expense Form ----------------
// const ExpenseForm = ({ voucher, onSave, onClose }) => {
//   const [accounts, setAccounts] = useState([]);
//   const [banks, setBanks] = useState([]);
//   const [staff, setStaff] = useState([]);
//   const [paymentModes, setPaymentModes] = useState([]);
//   const [isSaving, setIsSaving] = useState(false);

//   const defaultFormState = useMemo(() => ({
//     vrNo: "",
//     date: new Date().toISOString().split("T")[0],
//     paymentMode: "CASH",
//     paidThrough: "Admin",
//     paidFor: "",
//     bankName: "",
//     txnRefNo: "",
//     particulars: "",
//     preparedBy: "Admin",
//     checkedBy: "Admin",
//     expenseName: "",
//     expenseGroup: "",
//     amount: "",
//     entId: 0,
//     expenseId: 0,
//     bankId: 0,
//     paidRefId: 0,
//   }), []);

//   const [formData, setFormData] = useState(defaultFormState);

// useEffect(() => {
//     if (voucher) {
//         setFormData({
//             ...defaultFormState,
//             ...voucher,
//             entId: voucher.entId || 0, // crucial!
//             expenseId: voucher.expenseId || 0,
//             bankId: voucher.bankId || 0,
//             paidRefId: voucher.paidRefId || 0,
//         });
//     } else {
//         setFormData(defaultFormState);
//     }
// }, [voucher, defaultFormState]);



//   useEffect(() => {
//     const fetchFormData = async () => {
//       try {
//         const res = await api.GetExpenseVal("Admin");
//         const data = res?.GetExpenseValAPIjs || res;

//         setAccounts((data.Table3 || []).map(a => ({
//           id: a.HeadId,
//           name: a.HeadName,
//           group: a.AccGroup,
//           accId: a.AccId
//         })));

//         setStaff((data.Table || []).map(s => ({
//           id: s.Eid,
//           name: s.Ename,
//           code: s.Ecode
//         })));

//         setPaymentModes((data.Table1 || []).map(p => p.PMName));

//         setBanks((data.Table2 || []).map(b => ({
//           id: b.BankId,
//           name: b.BankName,
//           accNo: b.AccNo
//         })));

//       } catch (err) {
//         console.error("Failed to fetch form data:", err);
//       }
//     };
//     fetchFormData();
//   }, []);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     const updates = { [name]: value };

//     if (name === "expenseName") {
//       const selectedAccount = accounts.find(acc => acc.name === value);
//       updates.expenseGroup = selectedAccount ? selectedAccount.group : "";
//       updates.expenseId = selectedAccount ? selectedAccount.id : 0;
//     } else if (name === "bankName") {
//       const selectedBank = banks.find(b => b.name === value);
//       updates.bankId = selectedBank ? selectedBank.id : 0;
//     }

//     setFormData(prev => ({ ...prev, ...updates }));
//   };

//  const handleSubmit = async (e) => {
//   e.preventDefault();
//   setIsSaving(true);
//   try {
//     await onSave(formData); // ✅ formData is mapped in handleSaveVoucher
//     setIsSaving(false);
//   } catch (error) {
//     console.error("Error in handleSubmit:", error);
//     setIsSaving(false);
//   }
// };


//   return (
//     <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
//       <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
//         <form onSubmit={handleSubmit} className="p-6 sm:p-8">
//           {/* Header */}
//           <div className="flex items-center justify-between pb-4 mb-6 border-b">
//             <h2 className="text-2xl font-bold text-gray-700">
//               {voucher ? "Edit Expense" : "Create New Expense"}
//             </h2>
//             <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
//               <CloseIcon />
//             </button>
//           </div>

//           {/* Form Fields */}
//           <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
//             <div className="space-y-4">
//               <FormDatalist
//                 label="Expense Name"
//                 name="expenseName"
//                 value={formData.expenseName || ""}
//                 onChange={handleChange}
//                 options={accounts.map(a => a.name)}
//                 required
//               />
//               <FormInput label="Expense Group" name="expenseGroup" value={formData.expenseGroup} readOnly disabled />
//               <FormInput label="Amount" name="amount" type="number" value={formData.amount} onChange={handleChange} required />
//             </div>

//             <div className="space-y-4">
//               <FormInput label="Date" name="date" type="date" value={formData.date} onChange={handleChange} required />
//               <FormSelect label="Payment Mode" name="paymentMode" value={formData.paymentMode} onChange={handleChange} options={paymentModes} />
//               {formData.paymentMode !== "CASH" && (
//                 <>
//                   <FormDatalist label="Bank Name" name="bankName" value={formData.bankName} onChange={handleChange} options={banks.map(b => b.name)} required />
//                   <FormInput label="Txn Ref No / Chq No" name="txnRefNo" value={formData.txnRefNo} onChange={handleChange} />
//                 </>
//               )}
//               <FormInput label="Paid For" name="paidFor" value={formData.paidFor} onChange={handleChange} required />
//             </div>

//             <div className="space-y-4">
//               <FormDatalist label="Paid Through" name="paidThrough" value={formData.paidThrough} onChange={handleChange} options={staff.map(s => s.name)} />
//               <FormDatalist label="Prepared By" name="preparedBy" value={formData.preparedBy} onChange={handleChange} options={staff.map(s => s.name)} />
//               <FormDatalist label="Checked By" name="checkedBy" value={formData.checkedBy} onChange={handleChange} options={staff.map(s => s.name)} />
//             </div>

//             <div className="md:col-span-2 lg:col-span-3">
//               <FormTextArea label="Particulars / Remarks" name="particulars" value={formData.particulars} onChange={handleChange} />
//             </div>
//           </div>

//           <div className="flex justify-end gap-4 pt-6 mt-8 border-t">
//             <button type="submit" disabled={isSaving} className="px-3 py-1 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600">
//               {isSaving ? "Saving..." : "Save Expense"}
//             </button>
//             <button type="button" onClick={onClose} className="px-3 py-1 text-sm font-medium text-white bg-gray-500 rounded hover:bg-gray-600">
//               Cancel
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };


// /* ---------------- Reusable Form & List Components (No Changes) ---------------- */
// const ExpenseTable = ({ vouchers, onEdit }) => {
//     if (!vouchers || vouchers.length === 0)
//         return (
//             <div className="p-10 text-center bg-white rounded-lg shadow-md">
//                 <p>No expenses found. </p>
//             </div>
//         );

//     return (
//         <div className="overflow-x-auto bg-white shadow-md rounded-xl">
//             <table className="w-full text-sm text-left text-gray-500">
//                 <thead className="text-sm text-gray-900 uppercase bg-gray-100">
//                     <tr>
//                         <th className="px-6 py-3">Vr No</th>
//                         <th className="px-6 py-3">Date</th>
//                         <th className="px-6 py-3">Expense Name</th>
//                         <th className="px-6 py-3">Paid For</th>
//                         <th className="px-6 py-3">Mode</th>
//                         <th className="px-6 py-3 text-right">Amount</th>
//                         <th className="px-6 py-3 text-center">Actions</th>
//                     </tr>
//                 </thead>
//                 <tbody>
//                     {vouchers.map((v) => (
//                         <tr
//                             key={v.id}
//                             className="bg-white border-b hover:bg-gray-50"
//                         >
//                             <td className="px-6 py-4 font-medium text-gray-900">
//                                 {v.vrNo}
//                             </td>
//                             <td className="px-6 py-4">{v.date}</td>
//                             <td className="px-6 py-4">{v.expenseName}</td>
//                             <td className="px-6 py-4">{v.paidFor}</td>
//                             <td className="px-6 py-4">
//                                 <span
//                                     className={`px-2 py-1 text-xs font-medium rounded-full ${v.paymentMode === "CASH"
//                                             ? "bg-green-100 text-green-800"
//                                             : "bg-blue-100 text-blue-800"
//                                         }`}
//                                 >
//                                     {v.paymentMode}
//                                 </span>
//                             </td>
//                             <td className="px-6 py-4 text-right">
//                                 ₹
//                                 {parseFloat(v.amount || 0).toLocaleString("en-IN", {
//                                     minimumFractionDigits: 2,
//                                     maximumFractionDigits: 2,
//                                 })}
//                             </td>
//                             <td className="px-6 py-4 text-center">
//                                 <button
//                                     onClick={() => onEdit(v)}
//                                      className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium text-white 
//              bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
//              hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
//              transition-all duration-300 ease-in-out"
//                                 >
//                                      <FileEdit className="w-5 h-5" />
//                                     <span className="font-medium">Edit</span>
//                                 </button>
//                             </td>
//                         </tr>
//                     ))}
//                 </tbody>
//             </table>
//         </div>
//     );
// };

// const ExpenseCardList = ({ vouchers, onEdit }) => {
//     if (!vouchers || vouchers.length === 0)
//         return (
//             <div className="p-10 text-center bg-white rounded-lg shadow-md">
//                 <p>No expenses found. </p>
//             </div>
//         );

//     return (
//         <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
//             {vouchers.map((v) => (
//                 <div
//                     key={v.id}
//                     className="flex flex-col p-5 transition-shadow bg-white shadow-md rounded-xl hover:shadow-lg"
//                 >
//                     <div className="flex items-start justify-between mb-3">
//                         <h3 className="flex-1 pr-2 text-sm font-bold text-grey-700">
//                             {v.expenseName}
//                         </h3>
                       
//                     </div>
//                     <div className="mb-4 space-y-1 text-sm text-gray-700">
//                         <p>
//                             <strong>Vr No:</strong> {v.vrNo}
//                         </p>
//                         <p>
//                             <strong>Date:</strong> {v.date}
//                         </p>
//                         <p>
//                             <strong>Paid To:</strong> {v.paidFor}
//                         </p>
//                         <p>
//                             <strong>Mode:</strong>{" "}
//                             <span
//                                 className={`px-2 py-0.5 text-xs font-medium rounded-full ${v.paymentMode === "CASH"
//                                         ? "bg-green-100 text-green-800"
//                                         : "bg-blue-100 text-blue-800"
//                                     }`}
//                             >
//                                 {v.paymentMode}
//                             </span>
//                         </p>
//                         {v.paymentMode !== "CASH" && v.bankName && (
//                             <p>
//                                 <strong>Bank:</strong> {v.bankName}
//                             </p>
//                         )}
//                     </div>
//                     <div className="mt-auto text-right">
//                         <button
//                             onClick={() => onEdit(v)}
//                              className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium text-white 
//              bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
//              hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
//              transition-all duration-300 ease-in-out"
//                         >
//                            <FileEdit className="w-5 h-5" />
//                            <span className="font-medium">Edit</span>
//                         </button>
//                     </div>
//                 </div>
//             ))}
//         </div>
//     );
// };

// const FormInput = ({ label, ...props }) => (
//     <div>
//         <label className="block mb-1 text-sm font-medium text-gray-600">
//             {label}
//         </label>
//         <input
//             {...props}
//             className="w-full px-3 py-2 transition border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
//         />
//     </div>
// );

// const FormDatalist = ({ label, name, value, onChange, options, ...props }) => (
//     <div>
//         <label
//             htmlFor={name}
//             className="block mb-1 text-sm font-medium text-gray-600"
//         >
//             {label}
//         </label>
//         <input
//             id={name}
//             name={name}
//             list={`${name}-list`}
//             value={value || ""}
//             onChange={onChange}
//             {...props}
//             className="w-full px-3 py-2 transition border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
//         />
//         <datalist id={`${name}-list`}>
//             {options.map((opt) => (
//                 <option key={opt} value={opt} />
//             ))}
//         </datalist>
//     </div>
// );

// const FormSelect = ({ label, options, ...props }) => (
//     <div>
//         <label className="block mb-1 text-sm font-medium text-gray-600">
//             {label}
//         </label>
//         <select
//             {...props}
//             className="w-full px-3 py-2 transition border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
//         >
//             {options.map((opt) => (
//                 <option key={opt} value={opt}>
//                     {opt}
//                 </option>
//             ))}
//         </select>
//     </div>
// );

// const FormTextArea = ({ label, ...props }) => (
//     <div>
//         <label className="block mb-1 text-sm font-medium text-gray-600">
//             {label}
//         </label>
//         <textarea
//             {...props}
//             rows="3"
//             className="w-full px-3 py-2 transition border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
//         />
//     </div>
// );

